import i18next from 'i18next';
import { MUTATION_updateOrganizationSkills } from 'mutation';
import { QUERY_organizationSettingsPageSkills } from 'queries';
import { toastService } from 'services';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import {
  Mutation_UpdateOrganizationSkillsMutation,
  Mutation_UpdateOrganizationSkillsMutationVariables,
  MutationOutcomeStatusEnum,
  OrganizationSettingsPageSkillsQuery,
  OrganizationSettingsPageSkillsQueryVariables,
} from 'types/graphql';
import { handleGenericErrorMessage } from 'utils/handleGenericErrorMessage';
import { create } from 'zustand';
import {
  OrganizationSkillsData,
  OrganizationSkillsDraft,
  OrganizationSkillsState,
} from 'pages/Settings/pages/Organization/SkillsPage/store/organizationSkills.store.types';

const INITIAL_DRAFT: OrganizationSkillsDraft = {
  newSkills: [],
  removedSkillIds: [],
  renamedSkills: {},
  applyToAllLocationsChanges: {},
};

const INITIAL_STATE: OrganizationSkillsData = {
  ...INITIAL_DRAFT,
  organizationId: null,
  locationId: null,
  skills: [],
  isLoading: true,
  isSaving: false,
};

let fetchId = 0;
let newSkillKey = 0;

export const organizationSkillsStore = create<OrganizationSkillsState>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchSkills: (apolloClient, locationId) => {
      const currentFetchId = ++fetchId;
      set({ isLoading: true, locationId });

      apolloClient
        .query<OrganizationSettingsPageSkillsQuery, OrganizationSettingsPageSkillsQueryVariables>({
          query: QUERY_organizationSettingsPageSkills,
          variables: { locationId },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          if (currentFetchId !== fetchId) return;
          const organization = data.fetchCommunity.organization;
          set({
            organizationId: organization?.relayId ?? null,
            skills: organization?.skills ?? [],
            isLoading: false,
          });
        })
        .catch((error) => {
          if (currentFetchId !== fetchId) return;
          handleGenericErrorMessage(error);
          set({ isLoading: false });
        });

      return () => {
        fetchId++;
      };
    },

    addNewSkill: (name) =>
      set((state) => ({
        newSkills: [
          { key: `new-skill-${++newSkillKey}`, name: name.trim(), applyToAllLocations: false },
          ...state.newSkills,
        ],
      })),

    renameNewSkill: (key, name) =>
      set((state) => ({
        newSkills: state.newSkills.map((skill) => (skill.key === key ? { ...skill, name } : skill)),
      })),

    removeNewSkill: (key) =>
      set((state) => ({ newSkills: state.newSkills.filter((skill) => skill.key !== key) })),

    removeSkill: (displayId) =>
      set((state) => {
        const { [displayId]: _, ...otherRenamedSkills } = state.renamedSkills;
        const { [displayId]: __, ...otherApplyToAllLocationsChanges } =
          state.applyToAllLocationsChanges;
        return {
          removedSkillIds: [...state.removedSkillIds, displayId],
          renamedSkills: otherRenamedSkills,
          applyToAllLocationsChanges: otherApplyToAllLocationsChanges,
        };
      }),

    renameSkill: (displayId, name) =>
      set((state) => {
        const { [displayId]: _, ...otherRenamedSkills } = state.renamedSkills;
        const originalName = state.skills.find((skill) => skill.displayId === displayId)?.name;
        if (name === originalName) return { renamedSkills: otherRenamedSkills };
        return { renamedSkills: { ...otherRenamedSkills, [displayId]: name } };
      }),

    setApplyToAllLocations: (displayId, value) =>
      set((state) => {
        const { [displayId]: _, ...otherChanges } = state.applyToAllLocationsChanges;
        const originalValue = state.skills.find(
          (skill) => skill.displayId === displayId,
        )?.applyToAllLocations;
        if (value === originalValue) return { applyToAllLocationsChanges: otherChanges };
        return { applyToAllLocationsChanges: { ...otherChanges, [displayId]: value } };
      }),

    setNewSkillApplyToAllLocations: (key, value) =>
      set((state) => ({
        newSkills: state.newSkills.map((skill) =>
          skill.key === key ? { ...skill, applyToAllLocations: value } : skill,
        ),
      })),

    discardChanges: () => set(INITIAL_DRAFT),

    saveSkills: async (apolloClient) => {
      const {
        organizationId,
        locationId,
        newSkills,
        removedSkillIds,
        renamedSkills,
        applyToAllLocationsChanges,
      } = get();
      if (!organizationId || !locationId) return false;

      const saveFetchId = fetchId;
      const finishSave = () => {
        if (saveFetchId !== fetchId) return;
        set({ ...INITIAL_DRAFT, isSaving: false });
        get().fetchSkills(apolloClient, locationId);
      };

      set({ isSaving: true });

      try {
        const { data } = await apolloClient.mutate<
          Mutation_UpdateOrganizationSkillsMutation,
          Mutation_UpdateOrganizationSkillsMutationVariables
        >({
          mutation: MUTATION_updateOrganizationSkills,
          variables: {
            organizationId,
            skillsAttributes: [
              ...newSkills.map(({ name, applyToAllLocations }) => ({
                name: name.trim(),
                applyToAllLocations,
              })),
              ...Array.from(
                new Set([
                  ...Object.keys(renamedSkills),
                  ...Object.keys(applyToAllLocationsChanges),
                ]),
              ).map((id) => ({
                id: Number(id),
                ...(id in renamedSkills && { name: renamedSkills[Number(id)].trim() }),
                ...(id in applyToAllLocationsChanges && {
                  applyToAllLocations: applyToAllLocationsChanges[Number(id)],
                }),
              })),
              ...removedSkillIds.map((id) => ({ id, _destroy: true })),
            ],
          },
        });

        const payload = data?.updateOrganization;
        if (!payload || payload.errors?.length || payload.status !== MutationOutcomeStatusEnum.ok) {
          toastService.error(
            payload?.errors?.join(' ') || i18next.t('errors:SOMETHING_WENT_WRONG'),
          );
          finishSave();
          return false;
        }

        finishSave();
        toastService.success(i18next.t('success:SKILLS_SAVED'));
        return true;
      } catch (error) {
        handleGenericErrorMessage(error);
        if (saveFetchId === fetchId) set({ isSaving: false });
        return false;
      }
    },

    reset: () => set(INITIAL_STATE),
  })),
);

export const useOrganizationSkillsStore = organizationSkillsStore;
